import React, { useState, useEffect, useRef } from "react";
import { XMarkIcon, CameraIcon } from "@heroicons/react/24/outline";
import { ScreenshotSelector } from "./ScreenshotSelector";

interface CommentFormProps {
  position: { x: number; y: number };
  onSubmit: (text: string, screenshotBlob?: Blob) => void;
  onCancel: () => void;
}

export const CommentForm: React.FC<CommentFormProps> = ({
  position,
  onSubmit,
  onCancel,
}) => {
  const [text, setText] = useState("");
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure form is visible within viewport
    if (formRef.current) {
      const rect = formRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      if (rect.top < 0) {
        formRef.current.style.top = `${scrollY + 20}px`;
      }
      if (rect.left < 0) {
        formRef.current.style.left = `${scrollX + 20}px`;
      }
    }
  }, [position]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) {
      onSubmit(text, screenshotBlob || undefined);
      setText("");
      setScreenshotBlob(null);
    }
  };

  const handleScreenshotCapture = () => {
    setIsCapturingScreenshot(true);
  };

  const handleScreenshotCaptured = (blob: Blob) => {
    setScreenshotBlob(blob);
    setIsCapturingScreenshot(false);
  };

  const handleScreenshotCancel = () => {
    setIsCapturingScreenshot(false);
  };

  const removeScreenshot = () => {
    setScreenshotBlob(null);
  };

  return (
    <>
      {/* Solution 2: Style isolation wrapper */}
      <div
        className="feedbackflow-isolation-wrapper"
        style={{
          position: "fixed",
          top: position.y,
          left: position.x,
          transform: "translate(-50%, -100%)",
          marginTop: "-10px",
          zIndex: 2147483645,
          colorScheme: "light", // Solution 4: Force light color scheme
          // Solution 3: Inline style backup
          color: "#1f2937",
          fontSize: "14px",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div
          ref={formRef}
          className="feedbackflow-comment-form bg-white rounded-lg shadow-lg p-4 w-64"
          style={{
            // Solution 1: Explicit colors with high specificity
            backgroundColor: "#ffffff !important" as any,
            color: "#1f2937 !important" as any,
            border: "1px solid #e5e7eb !important" as any,
            // Solution 3: Inline backup
            background: "#ffffff",
          }}
          onClick={(e) => e.stopPropagation()}
          data-comment-form
        >
          <div className="flex justify-between items-center mb-2">
            <h3
              className="text-sm font-medium"
              style={{
                color: "#1f2937 !important" as any,
                fontSize: "14px !important" as any,
                fontWeight: "500 !important" as any,
              }}
            >
              Add Comment
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
              style={{
                color: "#6b7280 !important" as any,
                background: "none !important" as any,
                border: "none !important" as any,
              }}
            >
              <XMarkIcon
                className="w-4 h-4"
                style={{ color: "#6b7280 !important" as any }}
              />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full p-2 border border-gray-200 rounded-md text-sm mb-2"
              placeholder="Type your comment..."
              rows={3}
              autoFocus
              style={{
                // Solution 1: Force colors with !important
                backgroundColor: "#ffffff !important" as any,
                color: "#1f2937 !important" as any,
                border: "1px solid #e5e7eb !important" as any,
                fontSize: "14px !important" as any,
                // Solution 3: Inline backup
                background: "#ffffff",
              }}
            />

            {/* Screenshot section */}
            <div className="mb-2">
              {screenshotBlob ? (
                <div className="relative">
                  <img
                    src={URL.createObjectURL(screenshotBlob)}
                    alt="Selected screenshot"
                    className="w-full h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={removeScreenshot}
                    className="absolute top-1 right-1 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    style={{
                      backgroundColor: "#ef4444 !important" as any,
                      color: "#ffffff !important" as any,
                    }}
                  >
                    ×
                  </button>
                  <div
                    className="text-xs text-gray-500 mt-1"
                    style={{
                      color: "#6b7280 !important" as any,
                      fontSize: "12px !important" as any,
                    }}
                  >
                    📸 Screenshot attached
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleScreenshotCapture}
                  className="w-full p-2 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-indigo-400 hover:text-indigo-600 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: "transparent !important" as any,
                    color: "#6b7280 !important" as any,
                    border: "2px dashed #d1d5db !important" as any,
                    fontSize: "14px !important" as any,
                  }}
                >
                  <CameraIcon
                    className="w-4 h-4"
                    style={{ color: "#6b7280 !important" as any }}
                  />
                  Add Screenshot
                </button>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                style={{
                  color: "#6b7280 !important" as any,
                  background: "none !important" as any,
                  border: "none !important" as any,
                  fontSize: "14px !important" as any,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!text.trim()}
                className="px-3 py-1 text-sm bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                style={{
                  backgroundColor: "#4f46e5 !important" as any,
                  color: "#ffffff !important" as any,
                  fontSize: "14px !important" as any,
                }}
              >
                Post
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Screenshot selector overlay */}
      {isCapturingScreenshot && (
        <ScreenshotSelector
          onCapture={handleScreenshotCaptured}
          onCancel={handleScreenshotCancel}
        />
      )}
    </>
  );
};
